import { ContentService } from '@blk/cwp-ng-framework';
import { AbstractRenderer, Alignment, BoxModelConstraint, BusinessComponent, BusinessComponentOptions, ColumnPanel, RowPanel, StaticParagraphComponent } from '@blk/simba';
import { ChartFactory, ChartType, CommonChartUtil, ScatterChartCustomAttributes } from '@ddt-web/chart';
import { ColorsConfig } from '@ddt-web/config';
import { InjectConfig, SharedConfigKey } from '@ddt-web/config-layer';
import { ComponentData, TabData, TabUtil } from '@ddt-web/data-service';
import { DEFAULT_PERFORMANCE_DISCLOSURE_TEXT_CLASSES, PortfolioDotRenderer, ReportUtils } from '@ddt-web/pdf-utils';
import * as get from 'lodash/get';

import { DisclosureComponent } from '../../disclaimers/disclosure-component';
import { HighchartsSVGComponent } from '../../highchart/highchart-svg-component';
import { PerformanceTableUtil } from '../util';
import { PerformanceDisclaimerUtil } from '../util/performance-disclaimer.util';

export class HistoricalReturnsChart extends BusinessComponent {
	@InjectConfig(SharedConfigKey.EnableCustomFee) private readonly isCustomFeeEnabled: boolean = false;

	public scatterChartAttributes: ScatterChartCustomAttributes;

	private totalReturnsTabData: TabData;
	private netCustomReturnsTabData: TabData;
	private groupedLegendColumnConfiguration: Array<{}>;

	constructor(
		name: string,
		public dataComponentId: string,
		public customAdvisoryFee: number = null,
		public chartConstraints?: Partial<BoxModelConstraint>,
		public visible: boolean = true
	) {
		super(name, 'BusinessComponent', BusinessComponentOptions.merge({ visible: visible, componentId: dataComponentId }));
		if (!chartConstraints) {
			this.chartConstraints = this.isCustomFeeEnabled ? { height: 180, paddingBottom: 10 } : { height: 180, paddingBottom: 30 };
		}
	}

	public isComponentDataVisible(componentData: ComponentData): boolean {
		const isComponentDataSuccessful = componentData && componentData.success;
		this.totalReturnsTabData = isComponentDataSuccessful &&
			(componentData.getTabById('pa.returns.total.chart') || componentData.getTabById('returns.total.chart'));

		if (this.isCustomFeeEnabled && this.customAdvisoryFee) { // this.customAdvisoryFee will be null if excluded from the analysis
			this.netCustomReturnsTabData = isComponentDataSuccessful && componentData.getTabById('pa.custom.net.returns.total.chart');
		}

		const isGrossReturnsValid = TabUtil.isTabValid(this.totalReturnsTabData);
		const isCustomNetPerformanceValid = TabUtil.isTabValid(this.netCustomReturnsTabData);
		const isPerformanceValid = this.isCustomFeeEnabled && this.customAdvisoryFee ? isCustomNetPerformanceValid && isGrossReturnsValid : isGrossReturnsValid;

		return isPerformanceValid;
	}

	public buildComponent(componentData: ComponentData, contentSvc: ContentService, applicationState?: any): AbstractRenderer<SVGGElement>[] {
		const panels: AbstractRenderer<SVGGElement>[] = [];
		if (this.isComponentDataVisible(componentData)) {

			const dcrKeyPrefix = ReportUtils.createDcrKeyPrefixForPdf(this.name);

			const allSeriesData = componentData.tabs
				.reduce((acc: any, tabData: TabData) => {
					acc.push(...ChartFactory.getDefaultScatterSeriesForTabData([tabData]));
					return acc;
				}, [])
				.filter(series => series);

			this.groupedLegendColumnConfiguration = PerformanceTableUtil.getPerformanceChartGroupedLegendColumnConfigurationPdf(contentSvc, applicationState);

			this.scatterChartAttributes = {
				allSeriesData: allSeriesData,
				axisYMin: CommonChartUtil.findMinFromData(allSeriesData),
				multiSeriesConfig: CommonChartUtil.createCustomisationsArray(
					ColorsConfig.MULTI_SERIES_PORTFOLIO_COLORS
				)
			};
			if (this.dataComponentId === 'performance.returns.chart') {
				this.scatterChartAttributes['xAxis'] = {
					tickPositioner: function (): number[] {
						const positions = [];
						let tick = Math.floor(this.min);
						this.max = (this.tickAmount - 1) * this.tickInterval;
						for (tick; tick <= this.max; tick += this.tickInterval) {
							positions.push(tick);
						}
						return positions;
					}
				};
			}

			if (this.isCustomFeeEnabled) {
				panels.push(new RowPanel('Historical Returns Chart Section column panel',
					[
						this.getLegendPanel(),
						this.getChartPanel([this.totalReturnsTabData, this.netCustomReturnsTabData].filter(tab => tab))
					]
				));
			} else {
				panels.push(new RowPanel('Historical Returns Chart Section column panel',
					[
						this.getLegendPanel(),
						this.getChartPanel(this.totalReturnsTabData)
					]
				));
			}

			if (this.isCustomFeeEnabled) {
				if (!this.customAdvisoryFee) {
					panels.push(
						new DisclosureComponent(
							`NO_NET_PERF_DISCLOSURE`, 'NO_NET_PERF_DISCLOSURE', {}, { paddingTop: 5, paddingBottom: 10 }, DEFAULT_PERFORMANCE_DISCLOSURE_TEXT_CLASSES
						)
					);
				}
				panels.push(...PerformanceDisclaimerUtil.customFeeEnabledDisclaimer(dcrKeyPrefix + 'DISCLAIMER_'));
			} else {
				panels.push(
					PerformanceDisclaimerUtil.noCustomFeeEnabledDisclaimer(dcrKeyPrefix + 'DISCLAIMER')
				);
			}
		}
		return panels;
	}

	public getLegendPanel(): ColumnPanel {
		let renderer;
		let legendPanelItems;
		if (this.isCustomFeeEnabled) {
			legendPanelItems = [];
			[this.totalReturnsTabData, this.netCustomReturnsTabData].filter(tab => tab)
				.forEach((tab, tabIndex) => {
					legendPanelItems.push(
						...[
							// dcr is translated already in groupedLegendColumnConfiguration please see util for key
							new StaticParagraphComponent(
								`##NOT_APPLICABLE_DCR##`,
								this.groupedLegendColumnConfiguration[tabIndex]['heading'],
								{},
								{ paddingTop: tabIndex > 0 ? 5 : 0 },
								['text-bold']
							),
							this.groupedLegendColumnConfiguration[tabIndex]['subHeading'] ? new StaticParagraphComponent(
								`##NOT_APPLICABLE_DCR##`,
								this.groupedLegendColumnConfiguration[tabIndex]['subHeading']
							) : null
						].filter(item => item)
					);
					legendPanelItems.push(...get(tab, 'tableData[0].portfolioData', [])
						.filter((portfolio, index) => {
							const yVal = tab.tableData[0].cells[index][0].rawValue;
							const xVal = tab.tableData[0].cells[index][1].rawValue;
							return yVal && xVal;
						})
						.map((portfolio, index) => {
							renderer = new PortfolioDotRenderer(
								portfolio, index, Alignment.Horizontal.Left, 100, ['text-8'], {}, 9, ColorsConfig.MULTI_SERIES_PORTFOLIO_COLORS[tabIndex]
							);
							return new RowPanel('Legend item', [renderer.render()]);
						}));
				});
		} else {
			legendPanelItems = get(this.totalReturnsTabData, 'tableData[0].portfolioData', [])
				.filter((portfolio, index) => {
					const yVal = this.totalReturnsTabData.tableData[0].cells[index][0].rawValue;
					const xVal = this.totalReturnsTabData.tableData[0].cells[index][1].rawValue;
					return yVal && xVal;
				})
				.map((portfolio, index) => {
					renderer = new PortfolioDotRenderer(portfolio, index, Alignment.Horizontal.Left, 100, ['text-8'], {}, 9, ColorsConfig.MULTI_SERIES_PORTFOLIO_COLORS[0]);
					return new RowPanel('Legend item', [renderer.render()]);
				});
		}
		return new ColumnPanel('Legend panel', legendPanelItems, { proportion: 30 }, Alignment.Horizontal.Left, Alignment.Vertical.Top);
	}

	public getChartPanel(tabData): ColumnPanel {
		return new ColumnPanel(
			'Chart panel',
			[new HighchartsSVGComponent('Historical Returns Chart Section', this.chartConstraints, tabData, ChartType.Scatter, this.scatterChartAttributes)],
			{ proportion: 70 },
			Alignment.Horizontal.Left,
			Alignment.Vertical.Top
		);
	}
}
